@if (loaded) {
    <div class="flex flex-col h-full">
        <!-- Zone 1: Logo — always pinned to top -->
        <div
            class="flex-shrink-0 flex items-center justify-between py-4 lg:py-6 px-4 border-b border-surface-200 dark:border-surface-700">
            <img
                [src]="logoCompany"
                alt="Company Logo"
                class="w-auto max-h-12 lg:max-h-28 object-contain flex-1"
                width="200"
                height="112"
                decoding="async" />
            <!-- Close button — mobile only -->
            <button
                type="button"
                class="lg:hidden flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-surface-400 hover:text-surface-700 hover:bg-surface-100 transition-colors"
                ariaLabel="Close menu"
                (click)="closeMobileSidebar()">
                <i class="pi pi-times text-sm"></i>
            </button>
        </div>

        <!-- Zone 2: Dashboard — pinned below logo, not scrollable -->
        <div class="flex-shrink-0 px-2 pt-2">
            <a
                [routerLink]="dashboardItem.routerLink"
                (click)="itemClick($event, dashboardItem)"
                routerLinkActive="active-link"
                [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
                class="flex items-center cursor-pointer p-3 gap-4 transition-colors rounded-lg text-surface-700 dark:text-surface-200 [&:not(.active-link):not(.active-child)]:hover:bg-primary-200 dark:[&:not(.active-link):not(.active-child)]:hover:bg-primary-800">
                @if (dashboardItem.icon?.endsWith('.svg')) {
                    <span
                        class="nav-icon text-lg"
                        [style.--icon]="'url(' + dashboardItem.icon + ')'"></span>
                } @else {
                    <i [class]="dashboardItem.icon" class="text-lg"></i>
                }
                <span class="font-bold text-base">{{ dashboardItem.label }}</span>
            </a>
        </div>

        <!-- Zone 3: Scrollable menu items -->
        <div class="flex-1 overflow-y-auto min-h-0 px-2 pb-1 app-scrollbar">
            @if (groupedMenuItems.length > 0) {
                <div
                    class="sticky top-0 z-10 flex items-center justify-between px-3 py-1.5 mb-1 bg-surface-0 dark:bg-surface-900">
                    <span class="eyebrow">
                        Navigation
                    </span>
                    <button
                        type="button"
                        (click)="allExpanded ? collapseAll() : expandAll()"
                        class="flex items-center gap-1 text-label text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 transition-colors"
                        [title]="allExpanded ? 'Collapse all' : 'Expand all'">
                        <i
                            class="pi"
                            style="font-size: 0.6rem"
                            [class.pi-minus]="allExpanded"
                            [class.pi-plus]="!allExpanded"></i>
                        <span class="hidden sm:inline">
                            {{ allExpanded ? 'Collapse all' : 'Expand all' }}
                        </span>
                    </button>
                </div>
            }
            <ul class="list-none m-0 flex flex-col gap-1">
                @for (item of menuItems; track item.label) {
                    <li>
                        @if (item.items && item.items.length > 0) {
                            <button
                                type="button"
                                (click)="itemClick($event, item)"
                                [attr.aria-expanded]="isExpanded(item)"
                                class="flex items-center cursor-pointer p-3 gap-4 rounded-lg text-surface-900 dark:text-surface-0 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors w-full text-left bg-transparent border-0">
                                <span class="font-bold text-base">{{ item.label }}</span>
                                <i
                                    class="pi pi-angle-down text-base ml-auto transition-transform duration-200"
                                    [class.rotate-180]="isExpanded(item)"></i>
                            </button>

                            <ul
                                class="list-none m-0 overflow-hidden flex flex-col gap-1"
                                [ngClass]="{ hidden: !isExpanded(item) }">
                                @for (child of item.items; track child.label) {
                                    <!-- Set by MenuService on the first page of each group -->
                                    @if (child['groupHeading']) {
                                        <li class="eyebrow px-3 pt-3 pb-px">
                                            {{ child['groupHeading'] }}
                                        </li>
                                    }
                                    <li>
                                        <a
                                            [routerLink]="child.routerLink"
                                            routerLinkActive="bg-primary text-primary-contrast !text-white active-child"
                                            [routerLinkActiveOptions]="{ exact: false }"
                                            (click)="itemClick($event, child)"
                                            class="flex items-center cursor-pointer p-3 gap-2 rounded-lg transition-colors text-surface-700 dark:text-surface-200 [&:not(.active-link):not(.active-child)]:hover:bg-primary-200 dark:[&:not(.active-link):not(.active-child)]:hover:bg-primary-800">
                                            @if (child.icon?.endsWith('.svg')) {
                                                <span
                                                    class="nav-icon"
                                                    [style.--icon]="
                                                        'url(' + child.icon + ')'
                                                    "></span>
                                            } @else {
                                                <i [class]="child.icon"></i>
                                            }
                                            <span class="font-medium text-base">
                                                {{ child.label }}
                                            </span>
                                        </a>
                                    </li>
                                }
                            </ul>
                        } @else {
                            <a
                                [routerLink]="item.routerLink"
                                (click)="itemClick($event, item)"
                                routerLinkActive="active-link"
                                [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
                                class="flex items-center cursor-pointer p-3 gap-4 transition-colors rounded-lg text-surface-700 dark:text-surface-200 [&:not(.active-link):not(.active-child)]:hover:bg-primary-200 dark:[&:not(.active-link):not(.active-child)]:hover:bg-primary-800">
                                @if (item.icon?.endsWith('.svg')) {
                                    <span
                                        class="nav-icon text-lg"
                                        [style.--icon]="'url(' + item.icon + ')'"></span>
                                } @else {
                                    <i [class]="item.icon" class="text-lg"></i>
                                }
                                <span class="font-bold text-base">{{ item.label }}</span>
                            </a>
                        }
                    </li>
                }
            </ul>
        </div>

        <!-- Zone 4: Sign Out — always pinned to bottom -->
        <div
            class="flex-shrink-0 px-2 pt-1 pb-3 border-t border-surface-200 dark:border-surface-700">
            <a
                (click)="itemClick($event, signOutItem)"
                class="flex items-center cursor-pointer p-3 gap-4 transition-colors rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950">
                <i [class]="signOutItem.icon" class="text-lg"></i>
                <span class="font-bold text-base">{{ signOutItem.label }}</span>
            </a>
        </div>
    </div>
}
